/**
 * web/src/components/layout/Topbar.tsx
 * 56px topbar (handoff §App Shell §Topbar). Modes:
 *   - Title + dot + subtitle (default — used by WorkflowList, ApprovalQueue)
 *   - Breadcrumb (used by WorkflowDetail, GateReview)
 * Right side carries an optional `actions` slot (e.g., Trigger workflow
 * button on WorkflowList; Cancel + Retry on WorkflowDetail).
 *
 * Pages set their topbar content by calling `useSetTopbar({...})` from
 * inside their render. The provider lives at AppShell root; the effect
 * publishes content on mount and clears on unmount so navigations don't
 * leak stale titles.
 *
 * Why a context (not props passed through Outlet): React Router 7's
 * <Outlet> doesn't forward arbitrary props. Pages mount under a fixed
 * shell, so the topbar content has to flow from page → context → topbar.
 */
import { createContext, useContext, useState, useEffect, Fragment } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn';

/** Public shape pages publish via `useSetTopbar`. */
export interface TopbarContent {
  title?: string;
  subtitle?: string;
  breadcrumbs?: { label: string; to?: string }[];
  actions?: ReactNode;
}

const TopbarCtx = createContext<{
  content: TopbarContent;
  setContent: (c: TopbarContent) => void;
}>({ content: {}, setContent: () => {} });

/**
 * Wraps the AppShell subtree so pages can publish topbar content via
 * `useSetTopbar`. Mount once at the AppShell root.
 *
 * @param children render tree (typically <Sidebar /> + <main>)
 * @sideEffects holds topbar content state
 * @idempotent yes — multiple mounts at the same level (e.g., StrictMode
 *             double-invoke in dev) end up with stable state because
 *             pages re-publish on every effect run.
 */
export function TopbarProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<TopbarContent>({});
  return <TopbarCtx.Provider value={{ content, setContent }}>{children}</TopbarCtx.Provider>;
}

/**
 * Pages call this in their render to set the topbar content; the cleanup
 * resets to empty so navigations don't leak stale titles.
 *
 * The dep key is built from primitive fields only — `actions` is a ReactNode
 * (often JSX with circular fiber refs), so JSON.stringify on the whole
 * content throws "circular structure". We re-publish on every render where
 * the primitive fields change; pages keep `actions` stable enough via their
 * own render flow.
 *
 * @param content the title / subtitle / breadcrumbs / actions to publish
 * @sideEffects calls setContent on the TopbarCtx; resets on unmount
 */
export function useSetTopbar(content: TopbarContent) {
  const { setContent } = useContext(TopbarCtx);
  const depKey = JSON.stringify({
    title: content.title,
    subtitle: content.subtitle,
    breadcrumbs: content.breadcrumbs,
  });
  useEffect(() => {
    setContent(content);
    return () => setContent({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey]);
}

/**
 * Renders the topbar bar itself. Reads content from the TopbarCtx; pages
 * publish via useSetTopbar. Layout: 56px tall, 1px bottom border, 36px
 * horizontal padding (matches handoff main-area padding) at lg, 16px
 * at sm.
 */
export function Topbar({ className }: { className?: string }) {
  const { content } = useContext(TopbarCtx);
  const { title, subtitle, breadcrumbs, actions } = content;
  return (
    <header
      className={cn(
        'h-14 flex items-center px-9 sm:px-4 border-b border-border bg-bg shrink-0',
        className,
      )}
    >
      <div className="flex-1 flex items-center gap-2.5 min-w-0">
        {breadcrumbs && breadcrumbs.length > 0 ? (
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-2 text-[13px] text-text-muted truncate"
          >
            {breadcrumbs.map((c, i) => {
              const isLast = i === breadcrumbs.length - 1;
              // A crumb with `to` that isn't the current page renders as real
              // navigation; the last crumb (current page) and target-less
              // crumbs render as plain non-interactive text.
              const crumb =
                c.to && !isLast ? (
                  <Link
                    to={c.to}
                    className="truncate rounded-sm hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
                  >
                    {c.label}
                  </Link>
                ) : (
                  <span className={cn('truncate', isLast && 'text-text font-semibold')}>
                    {c.label}
                  </span>
                );
              return (
                <Fragment key={i}>
                  {i > 0 && <span className="text-text-faint mx-2">/</span>}
                  {crumb}
                </Fragment>
              );
            })}
          </nav>
        ) : (
          <>
            <h1 className="text-[15px] font-semibold -tracking-[0.01em] truncate">{title ?? ''}</h1>
            {subtitle && (
              <>
                <span className="text-text-faint">·</span>
                <span className="text-[13px] text-text-muted truncate">{subtitle}</span>
              </>
            )}
          </>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">{actions}</div>
    </header>
  );
}
